import {
    Button,
    Card,
    DialogActions,
    DialogTitle,
    Divider,
    Modal,
    ModalClose,
    ModalDialog,
    Stack,
    Typography, useTheme
} from "@mui/joy";
import React, {useEffect, useState} from "react";
import IconBoxComponent from "./IconBoxComponent";
import {Car, CheckIcon, Lock} from "lucide-react";
import {subscriptions} from "../values/mockSubscriptions";
import {useLanguage} from "../hooks/useLanguage";
import {useAsync} from "../hooks/useAsyncResult";
import {getSubscriptions} from "../services/api/subscription";
import LoadingPageComponentSmall from "./Loading/LoadingPageComponentSmall";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import LoadingError from "./Error/LoadingError";

const PricingDialog = (props: {
    open?: boolean,
    onClose?: () => void,
    sticky?: boolean,
}) => {
    const theme = useTheme()
    const {t} = useTranslation()
    const [selectedIndex, setSelectedIndex] = useState<number>(0)
    const language = useLanguage()
    const subscriptionAsyncResult = useAsync(getSubscriptions, true)
    const navigate = useNavigate()

    const handleOptOut = () => {
        navigate(-1)
    }

    useEffect(() => {
        subscriptionAsyncResult.execute()
    }, [])

    return <Modal open={props.open} onClose={props.onClose}>
        <ModalDialog variant="outlined" role="alertdialog" size={"sm"}
                     sx={{
                         minWidth: {xs: "100%", sm: "30rem"},
                         minHeight: {xs: "100%", sm: "max-content"},
                         borderRadius: 25,
                         paddingTop: 0,
                         gap: 0,
                         overflow: "hidden",
                         paddingX: 0
                     }}>

            {!props.sticky && <ModalClose/>}

            <Stack alignItems={"center"} gap={1} paddingY={2} sx={{backgroundColor: "primary.50"}}>
                <IconBoxComponent color={"primary"} style={{backgroundColor: "primary.500", borderRadius: "50%"}}>
                    <Lock style={{fontSize: 40, color: "white"}}/>
                </IconBoxComponent>

                <Stack alignItems={"center"}>
                    <Typography level={"title-lg"}>
                        {t("subscriptionPromptTitle")}
                    </Typography>

                    <Typography level={"body-sm"}>
                        {t("subscriptionPromptDescription")}
                    </Typography>
                </Stack>

            </Stack>

            {!subscriptionAsyncResult.isSuccess && !subscriptionAsyncResult.loading &&
                <LoadingError label={t("pricingLabel")} onRetry={subscriptionAsyncResult.execute}/>}

            {subscriptionAsyncResult.loading ?
                <LoadingPageComponentSmall loadingText={t("plainLoadingText")}/> :
                <>
                    <Stack padding={2} gap={1} direction={"row"} overflow={"auto"} flexWrap={"wrap"}>
                        {subscriptionAsyncResult.payload?.map((subscription, index) =>
                            <Card variant={index === selectedIndex ? "soft" : "outlined"}
                                  color={index === selectedIndex ? "primary" : "neutral"}
                                  onClick={() => setSelectedIndex(index)}
                                  sx={{
                                      borderRadius: 15,
                                      cursor: "pointer",
                                      width: "6rem",
                                      gap: 0,
                                  }}>
                                <Typography level={"body-sm"}>
                                    {t("subscriptionDaysLabel", {dayCount: subscription.validity})}
                                </Typography>

                                {subscription.isTrial ?
                                    <>
                                        <Typography level={"title-md"} fontWeight={"bold"}>
                                            {t("subscriptionFreeTrialLabel")}
                                        </Typography>

                                        <Typography level={"body-sm"}>
                                            {t("subscriptionFreeTrialActionLabel")}
                                        </Typography>
                                    </>
                                    :
                                    <>
                                        <Typography level={"title-md"} fontWeight={"bold"}>
                                            {subscription.localizedPrice}
                                        </Typography>
                                        <Typography level={"body-xs"}>
                                            {t("subscriptionPaymentTypeLabel")}
                                        </Typography>
                                    </>}

                            </Card>)}
                    </Stack>

                    <Stack paddingX={2} paddingTop={1} gap={1}>
                        {subscriptionAsyncResult.payload?.[selectedIndex].features.map((feat, index, array) =>
                            <Stack>
                                <Typography startDecorator={<CheckIcon style={{color: theme.palette.primary['600']}}/>}>
                                    {feat[language.lang]}
                                </Typography>
                                {index !== array.length - 1 && <Divider/>}
                            </Stack>)}
                    </Stack>

                    <DialogActions sx={{paddingTop: 2, paddingX: 2}}>
                        <Stack gap={1} width={"100%"}>
                            <Button component={Link}
                                    to={`/checkout?subscriptionId=${subscriptionAsyncResult.payload?.[selectedIndex]._id}`}
                                    size={"lg"}>
                                {t("subscriptionPromptPositiveActionLabel", {subscriptionPlan: subscriptionAsyncResult.payload?.[selectedIndex].name})}
                                {!subscriptionAsyncResult.payload?.[selectedIndex].isTrial && " - " + subscriptionAsyncResult.payload?.[selectedIndex].localizedPrice}
                            </Button>

                            <Button onClick={handleOptOut} variant={"outlined"} color={"neutral"}>
                                {t("subscriptionPromptNegativeActionLabel")}
                            </Button>
                            <Typography textAlign={"center"} level={"body-sm"}>
                                {t("subscriptionPromptFooterNote")}
                            </Typography>
                        </Stack>
                    </DialogActions>
                </>}

        </ModalDialog>
    </Modal>
}

export default PricingDialog